import { Alert, AutoComplete, Button, Popover, Space, Tooltip } from "antd";
import { MessageFilled, MessageOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import styled from "styled-components";
import {
  COMMENT_PRESETS,
  isAbsentComment,
  isInvalidPresentComment,
} from "./rollUtils";

const PopoverContent = styled.div`
  width: 300px;
`;

const Actions = styled.div`
  margin-top: 8px;
  text-align: right;
`;

const RollCommentPopover: React.FC<{
  comment: string;
  isPresent: boolean;
  isDisabled: boolean;
  /** Absent comments automatically mark the student as absent. */
  onSave: (comment: string, isPresent: boolean) => Promise<void>;
}> = ({ comment, isPresent, isDisabled, onSave }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [value, setValue] = useState(comment);
  const [error, setError] = useState<string>();
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!isVisible) {
      setValue(comment);
      setError(undefined);
    }
  }, [comment, isVisible]);

  const save = async (newComment: string) => {
    const trimmedComment = newComment.trim();
    const newIsPresent = isAbsentComment(trimmedComment) ? false : isPresent;
    if (newIsPresent && isInvalidPresentComment(trimmedComment)) {
      setError(
        "This comment can only be used for students who were absent (it contains the word “without”). Untick the student first or change the comment."
      );
      return;
    }

    setIsSaving(true);
    try {
      await onSave(trimmedComment, newIsPresent);
      setIsVisible(false);
    } catch (error) {
      setError(error.message);
    } finally {
      setIsSaving(false);
    }
  };

  const content = (
    <PopoverContent>
      <AutoComplete
        style={{ width: "100%" }}
        value={value}
        options={COMMENT_PRESETS.map((preset) => ({ value: preset }))}
        onChange={(newValue) => setValue(newValue)}
        placeholder="e.g. reason for being absent"
        disabled={isDisabled}
        // Render the options inside the popover so selecting one doesn't close the popover.
        getPopupContainer={(trigger) => trigger.parentElement}
      />
      {error && (
        <Alert message={error} type="error" showIcon style={{ marginTop: 8 }} />
      )}
      <Actions>
        <Space>
          <Button
            size="small"
            disabled={isDisabled || comment.length === 0}
            loading={isSaving}
            onClick={() => save("")}
          >
            Clear
          </Button>
          <Button
            size="small"
            type="primary"
            disabled={isDisabled}
            loading={isSaving}
            onClick={() => save(value)}
          >
            Save
          </Button>
        </Space>
      </Actions>
    </PopoverContent>
  );

  return (
    <Popover
      content={content}
      title="Comment"
      trigger="click"
      visible={isVisible}
      onVisibleChange={(visible) => !isDisabled && setIsVisible(visible)}
    >
      <Tooltip title={comment || "Add comment"}>
        <Button
          size="small"
          type="text"
          disabled={isDisabled}
          icon={comment ? <MessageFilled /> : <MessageOutlined />}
        />
      </Tooltip>
    </Popover>
  );
};

export default RollCommentPopover;
